import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useLocation, Link } from 'react-router-dom';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import RightArrow from 'components/ClientResources/images/blue-right-arrow.svg';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full.svg';
import IconButton from '@material-ui/core/IconButton';
import Mixpanel from 'utils/mixpanelService';
import { getImageFile, fallbackImage, isBot } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  cardLink: {
    textDecoration: 'none',
  },
  wrapper: {
    width: '100%',
    padding: '15px 8px 15px 8px',
    height: 100,
    borderRadius: 4,
    display: 'inline-flex',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      paddingRight: 12,
    },
  },
  removePadding: {
    padding: 0,
  },
  shadow: {
    boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.17)',
  },
  withLearnMore: {
    height: 'auto',
  },
  fullHeight: {
    height: '100%',
  },
  textContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // height: '100%',
    flex: 1,
  },
  title: {
    width: '100%',
    fontSize: 14,
    lineHeight: '17px',
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 34 /* Fallback for non-webkit */,
    wordBreak: 'break-word',
    marginBottom: 4,
    paddingRight: 12,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 8,
    },
  },
  subtitle: {
    width: '100%',
    fontSize: 12,
    lineHeight: '16px',
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 32 /* Fallback for non-webkit */,
    wordBreak: 'break-word',
  },
  textPadding: {
    paddingRight: 8,
  },
  img: {
    width: '68px !important',
    height: '68px !important',
    borderRadius: 10,
    marginRight: 8,
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      marginRight: 12,
    },
  },
  icon: {
    position: 'absolute',
    top: 7,
    right: 6,
    padding: 0,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  heart: {
    width: 17,
    zIndex: 2,
  },
  learnMoreLabel: {
    display: 'flex',
    marginTop: 'auto',
  },
  learnMoreIcon: {
    marginLeft: 4,
  },
}));

const TopicCardCTA = ({
  id,
  fields,
  fields: { slug, title, description },
  allowSignIn,
  onSave,
  isSaved,
  showLearnMore,
  removeShadow,
  fullHeight,
  textPadding,
  removePadding,
}) => {
  const classes = useStyles();
  const location = useLocation();

  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: title,
      type: 'topic',
      page: finalPageName,
      path: location.pathname.split('/')[1],
      slug: location.pathname.split('/')[2],
    });
  };

  const redirectUrl = `/topics/${slug}`;
  const hasTopicImage =
    !_isNil(_get(fields, 'image.fields.file.url')) ||
    !_isNil(_get(fields, 'imageUrl')) ||
    !_isNil(_get(fields, 'image.url'));
  const imgUrl = hasTopicImage ? getImageFile(fields) : fallbackImage;
  const bot = isBot();

  return (
    <Grid
      item
      xs={12}
      className={_classNames({
        [classes.fullHeight]: fullHeight,
      })}
    >
      <Link to={redirectUrl} onClick={redirect} className={classes.cardLink}>
        <div
          className={_classNames({
            [classes.wrapper]: true,
            [classes.removePadding]: removePadding,
            [classes.shadow]: !removeShadow,
            [classes.withLearnMore]: !fullHeight && showLearnMore,
            [classes.fullHeight]: fullHeight,
          })}
        >
          {!bot && allowSignIn && isSaved && (
            <IconButton
              className={classes.icon}
              onClick={e => onSave(id, fields, true, e)}
              disableRipple
              disableFocusRipple
            >
              <img src={HeartFullIcon} alt="Full" className={classes.heart} />
            </IconButton>
          )}
          {!bot && allowSignIn && !isSaved && (
            <IconButton
              className={classes.icon}
              onClick={e => onSave(id, fields, false, e)}
              disableRipple
              disableFocusRipple
            >
              <img src={HeartEmptyIcon} alt="Empty" className={classes.heart} />
            </IconButton>
          )}
          <img src={imgUrl} className={classes.img} alt="Topic" />
          <div className={classes.textContainer}>
            <Typography
              variant="caption"
              align="left"
              color="textPrimary"
              className={classes.title}
            >
              {title}
            </Typography>
            <Typography
              variant="overline"
              align="left"
              color="textPrimary"
              className={_classNames({
                [classes.subtitle]: true,
                [classes.textPadding]: textPadding,
              })}
            >
              {description}
            </Typography>
            {showLearnMore && (
              <Typography
                variant="overline"
                align="left"
                color="textSecondary"
                className={classes.learnMoreLabel}
              >
                Learn More
                <img
                  src={RightArrow}
                  alt=""
                  className={classes.learnMoreIcon}
                />
              </Typography>
            )}
          </div>
        </div>
      </Link>
    </Grid>
  );
};

TopicCardCTA.propTypes = {
  id: PropTypes.string,
  fields: PropTypes.object,
  allowSignIn: PropTypes.bool,
  onSave: PropTypes.func,
  isSaved: PropTypes.bool,
  showLearnMore: PropTypes.bool,
  removeShadow: PropTypes.bool,
  fullHeight: PropTypes.bool,
  textPadding: PropTypes.bool,
  removePadding: PropTypes.bool,
};

TopicCardCTA.defaultProps = {
  showLearnMore: false,
  removeShadow: false,
  fullHeight: false,
  textPadding: false,
  removePadding: false,
};

export default TopicCardCTA;
